<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import Calendar from '@/components/Stats/Calendar.vue'
import UIKit from '@pocketprep/ui-kit'
import Loading from '@/components/Loading.vue'
import { statsModule } from '@/store/stats/module'
import { analyticsModule } from '@/store/analytics/module'

const { SegmentControl } = UIKit

const displayCalendar = ref<'all' | 'qotd'>('all')

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const streakStats = computed(() => {
    const streakDates = displayCalendar.value === 'all'
        ? currentStudentStats.value.datesActive
        : currentStudentStats.value.qotdActivity.map(activity => activity.date)

    return streakDates.reduce((acc, date, index, datesActive) => {
        const previousDate = datesActive[index - 1]
        if (!previousDate || (new Date(date).getTime() - new Date(previousDate).getTime() === 86400000)) {
            acc.currentStreak.push(date)
        } else {
            acc.streaks.push(acc.currentStreak)
            acc.currentStreak = [ date ]
        }

        const nextDate = datesActive[index + 1]
        if (!nextDate) {
            acc.streaks.push(acc.currentStreak)
            if (new Date(new Date().toLocaleDateString()).getTime() - new Date(date).getTime() > 86400000) {
                acc.currentStreak = []
            }
        }

        return acc
    }, { currentStreak: [] as string[], streaks: [] as string[][] })
})

const currentStreakLength = computed(() => {
    return streakStats.value.currentStreak.length
})

const longestStreakLength = computed(() => {
    return streakStats.value.streaks.reduce((longest, streak) => Math.max(streak.length, longest), 0)
})

const toggleCalendar = (option: number) => {
    const optionName = option === 1 ? 'all' : 'qotd'
    displayCalendar.value = optionName

    analyticsModule.actions.amplitudeTrack('Segment Control Clicked', {
        segmentControlContext: 'Student Stats Study Streaks',
        segmentControlValue: optionName,
    })
}

onMounted(async () => {
    await statsModule.actions.fetchCurrentStudentStats()
})
</script>

<template>
    <div class="streaks" :class="{ 'streaks--loading': currentStudentStats.isLoading }">
        <SectionHead
            title="Study Streaks"
            icon="lightning"
        >
            <SegmentControl
                option1="All Studying"
                option2="QOTD"
                @change="toggleCalendar"
            />
        </SectionHead>

        <Loading v-if="currentStudentStats.isLoading" />
        <div v-else class="streaks__calendar">
            <div class="streaks__calendar-stats">
                <div class="streaks__calendar-current">
                    <span>Current</span> {{ currentStreakLength }} day{{ currentStreakLength === 1 ? '' : 's' }}
                </div>
                <div class="streaks__calendar-longest">
                    <span>Longest</span> {{ longestStreakLength }} day{{ longestStreakLength === 1 ? '' : 's' }}
                </div>
            </div>
            <Calendar v-if="displayCalendar === 'all'" :activities="currentStudentStats.datesActive" />
            <Calendar v-else :activities="currentStudentStats.qotdActivity" />
        </div>
    </div>
</template>

<style lang="scss" scoped>


.streaks {
    .streaks__calendar {
        margin-top: 22px;

        .streaks__calendar-stats {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            letter-spacing: 0.2px;
            line-height: 18px;
            margin-bottom: 26px;

            span {
                font-weight: 400;
            }
        }
    }

    &--loading {
        .streaks__calendar {
            filter: blur(4px);
        }
    }
}
</style>
